import { provideAbpCore, withOptions } from '@abp/ng.core';
import { BrowserTokenStorageService, provideAbpOAuth } from '@abp/ng.oauth';
import { provideSettingManagementConfig } from '@abp/ng.setting-management/config';
import { provideFeatureManagementConfig } from '@abp/ng.feature-management';
import {
  provideAbpThemeShared,
  provideLogo,
  withEnvironmentOptions,
  withHttpErrorConfig,
} from '@abp/ng.theme.shared';
import { provideIdentityConfig } from '@volo/abp.ng.identity/config';
import { provideCommercialUiConfig } from '@volo/abp.commercial.ng.ui/config';
import { provideAccountAdminConfig } from '@volo/abp.ng.account/admin/config';
import { provideAccountPublicConfig } from '@volo/abp.ng.account/public/config';
import { provideGdprConfig, withCookieConsentOptions } from '@volo/abp.ng.gdpr/config';
import { provideAuditLoggingConfig } from '@volo/abp.ng.audit-logging/config';
import { provideLanguageManagementConfig } from '@volo/abp.ng.language-management/config';
import { provideSaasConfig } from '@volo/abp.ng.saas/config';
import { provideTextTemplateManagementConfig } from '@volo/abp.ng.text-template-management/config';
import { provideOpeniddictproConfig } from '@volo/abp.ng.openiddictpro/config';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { provideAnimations } from '@angular/platform-browser/animations';
import { OAuthStorage } from 'angular-oauth2-oidc';
import { provideRouter, TitleStrategy } from '@angular/router';
import { environment } from '../environments/environment';
import { APP_ROUTES } from './app.routes';
import { APP_ROUTE_PROVIDER } from './route.provider';
import { CustomErrorComponent } from '@shared/custom-error/custom-error-component';
import { LayoutStateService, provideThemeLepton } from '@volo/abp.ng.theme.lepton';
import { registerLocale } from '@volo/abp.ng.language-management/locale';
import { PSTCompleteAppTitleStrategy } from '@shared/PSTCompleteAppTitleStrategy';
import { installJavascriptHrefCspMitigation } from '@shared/services/csp-javascript-href-mitigation';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(APP_ROUTES),
    APP_ROUTE_PROVIDER,
    provideAbpCore(
      withOptions({
        environment,
        registerLocaleFn: registerLocale(),
      }),
    ),
    provideAnimations(),
    provideAbpOAuth(),
    {
      provide: OAuthStorage,
      useExisting: BrowserTokenStorageService,
    },
    provideIdentityConfig(),
    provideSettingManagementConfig(),
    provideFeatureManagementConfig(),
    provideAccountAdminConfig(),
    provideAccountPublicConfig(),
    provideCommercialUiConfig(),
    provideThemeLepton(),
    provideAbpThemeShared(
      withHttpErrorConfig({
        errorScreen: {
          component: CustomErrorComponent,
          forWhichErrors: [401, 403, 404, 500],
          hideCloseIcon: true,
        },
      }),
    ),
    {
      provide: TitleStrategy,
      useClass: PSTCompleteAppTitleStrategy,
    },
    provideLogo(withEnvironmentOptions(environment)),
    provideGdprConfig(
      withCookieConsentOptions({
        cookiePolicyUrl: '/gdpr-cookie-consent/cookie',
        privacyPolicyUrl: '/gdpr-cookie-consent/privacy',
      }),
    ),
    provideLanguageManagementConfig(),
    provideSaasConfig(),
    provideAuditLoggingConfig(),
    provideOpeniddictproConfig(),
    provideTextTemplateManagementConfig(),
    provideAppInitializer(() => {
      const layoutStateService = inject(LayoutStateService);
      const document = inject(DOCUMENT);

      //installJavascriptHrefCspMitigation(document); # Currently causes issues for the main navigation menu. It causes a page reload when clicking on the main menu header items. We will investigate and re-enable it later.

      layoutStateService.patch({ style: 4 });
    }),
  ],
};
